// Copyright (c) Meta Platforms, Inc. and affiliates.

'use client';

/**
 * @file NumberInput.tsx
 * @input Uses React, useId, useState, useMemo, useCallback, Field, Icon, InputGroupContext
 * @output Exports NumberInput component, NumberInputProps
 * @position Core implementation; consumed by index.ts, tested by NumberInput.test.tsx
 *
 * SYNC: When modified, update these files to stay in sync:
 * - /packages/core/src/NumberInput/NumberInput.doc.mjs (props table, features, implementation notes)
 * - /packages/core/src/NumberInput/NumberInput.test.tsx (tests for new/changed behavior)
 * - /packages/core/src/NumberInput/index.ts (exports if types change)
 * - /apps/storybook/stories/NumberInput.stories.tsx (storybook stories)
 * - /packages/cli/templates/blocks/components/NumberInput/ (showcase blocks)
 */
import { useId, useState, useMemo, useCallback, useRef } from 'react';
import * as stylex from '@stylexjs/stylex';
import "../theme/tokens.stylex.js";
import { colorVars, sizeVars, radiusVars, typographyVars, typeScaleVars, borderVars } from "../theme/tokens.stylex.js";
import { Field, inputWrapperStyles, inputStatusBorderStyles, inputStatusHoverShadowStyles, inputStatusFocusWithinStyles } from "../Field/index.js";
import { Icon, renderIconSlot } from "../Icon/index.js";
import { VisuallyHidden } from "../VisuallyHidden/index.js";
import { useTooltip } from "../Tooltip/index.js";
import { getInputARIA } from "../utils/index.js";
import { useSize } from "../SizeContext/SizeContext.js";
import { useInputContainer } from "../hooks/useInputContainer.js";
import { useInputGroup } from "../InputGroup/InputGroupContext.js";
const styles = {
  wrapper: {
    kY2c9j: "x1vjfegm",
    $$css: true
  }
};
const sizeStyles = {
  sm: {
    kZKoxP: "x6k0iem",
    $$css: true
  },
  md: {
    kZKoxP: "x1ueg155",
    $$css: true
  },
  lg: {
    kZKoxP: "xssyfek",
    $$css: true
  }
};
import { groupStyles } from "../InputGroup/groupStyles.js";

// Re-export shared types for convenience

import { mergeProps, mergeRefs } from "../utils/index.js";
import { themeProps } from "../utils/themeProps.js";
import { useTranslator } from "../i18n/index.js";

/**
 * Without `hasClear`, onChange only receives valid numbers.
 * With `hasClear`, onChange also receives `null` when the user clears the input.
 */
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * Parse and validate a string input as a number.
 * Returns null if the input is not a valid number or fails validation.
 */
function parseNumberInput(input, options) {
  const trimmed = input.trim();
  if (trimmed === '' || trimmed === '-') {
    return null;
  }
  const num = Number(trimmed);
  if (!Number.isFinite(num)) {
    return null;
  }

  // Check integer constraint
  if (options.isIntegerOnly && !Number.isInteger(num)) {
    return null;
  }

  // Check min constraint
  if (options.min != null && num < options.min) {
    return null;
  }

  // Check max constraint
  if (options.max != null && num > options.max) {
    return null;
  }
  return num;
}

/**
 * A number input component for collecting numeric user input.
 * Only calls onChange when the entered value passes validation.
 *
 * @example
 * ```
 * <NumberInput label="Quantity" value={quantity} onChange={setQuantity} />
 * <NumberInput label="Price" value={price} onChange={setPrice} min={0} step={0.01} />
 * ```
 */
export function NumberInput({
  label,
  isLabelHidden = false,
  description,
  isOptional = false,
  isRequired = false,
  isDisabled = false,
  disabledMessage,
  startIcon,
  labelIcon,
  status,
  size: sizeProp,
  onChange,
  value,
  placeholder,
  labelTooltip,
  hasAutoFocus = false,
  htmlName,
  autoComplete,
  min,
  max,
  step,
  units,
  isIntegerOnly = false,
  onFocus,
  onBlur,
  hasClear,
  onEnter,
  onKeyDown,
  width,
  xstyle,
  className,
  style,
  ref,
  ...rest
}) {
  const t = useTranslator();
  const size = useSize(sizeProp, 'md');
  const id = useId();
  const inputLabelID = useId();
  const descriptionID = useId();
  const statusMessageID = useId();
  const inputRef = useRef(null);
  const containerRef = useRef(null);
  const inputGroup = useInputGroup();

  // Pending input while user is typing (null = show formatted value)
  const [pendingInput, setPendingInput] = useState(null);

  // Disabled-reason tooltip. Disabled controls swallow pointer events, so the
  // tooltip listeners attach to the input container (which already exists) and
  // the input stays perceivable via aria-disabled instead of the native
  // disabled attribute. The field is made read-only so it can't be typed into,
  // and value mutation is blocked by the isDisabled guard in the handlers.
  const showsDisabledMessage = isDisabled && !!disabledMessage;
  const disabledMessageTooltip = useTooltip({
    placement: 'above',
    // The container div is not naturally focusable; focusin bubbles up from
    // the input, so always attach focus listeners.
    focusTrigger: 'always',
    isEnabled: showsDisabledMessage
  });
  const statusIconMap = {
    warning: 'warning',
    error: 'error',
    success: 'success'
  };
  const statusIconColorMap = {
    warning: 'warning',
    error: 'error',
    success: 'success'
  };
  const {
    ariaLabelledBy,
    ariaDescribedBy
  } = getInputARIA(inputLabelID, [description ? descriptionID : null, status?.message ? statusMessageID : null, showsDisabledMessage ? disabledMessageTooltip.describedBy : null], inputGroup);

  // Display value: pending input if typing, otherwise the raw value
  // Note: With type="number", we can't use formatted display values
  const displayValue = useMemo(() => {
    if (pendingInput !== null) {
      return pendingInput;
    }
    if (value == null) {
      return '';
    }
    return String(value);
  }, [pendingInput, value]);

  // Check if current pending input is valid (for styling purposes)
  const isInputValid = useMemo(() => {
    if (pendingInput === null || !pendingInput.trim()) {
      return true;
    }
    return parseNumberInput(pendingInput, {
      min,
      max,
      isIntegerOnly
    }) !== null;
  }, [pendingInput, min, max, isIntegerOnly]);

  // Handle input text change - update immediately if valid
  const handleInputChange = useCallback(e => {
    // Value can't change while showing a disabled message (the field is
    // read-only and non-native-disabled), but guard the handler too so the
    // pending value and onChange never fire.
    if (isDisabled) {
      return;
    }
    const newValue = e.target.value;
    setPendingInput(newValue);

    // If the input is valid, update immediately
    const parsed = parseNumberInput(newValue, {
      min,
      max,
      isIntegerOnly
    });
    if (parsed !== null && parsed !== value) {
      onChange(parsed);
    }
  }, [value, onChange, min, max, isIntegerOnly, isDisabled]);

  // Handle focus
  const handleFocus = useCallback(e => {
    onFocus?.(e);
  }, [onFocus]);

  // Handle blur - validate and clear pending input
  const handleBlur = useCallback(e => {
    if (pendingInput !== null) {
      if (hasClear && pendingInput.trim() === '') {
        // Keyboard clearing honors the clearable contract: an emptied
        // input commits null instead of silently reverting on blur.
        if (value != null) {
          onChange(null);
        }
      } else {
        const parsed = parseNumberInput(pendingInput, {
          min,
          max,
          isIntegerOnly
        });
        if (parsed !== null && parsed !== value) {
          onChange(parsed);
        }
      }
    }

    // Clear pending input - display will revert to formatted value
    setPendingInput(null);
    onBlur?.(e);
  }, [pendingInput, value, onChange, min, max, isIntegerOnly, onBlur, hasClear]);

  // Handle keyboard events
  const handleKeyDown = useCallback(e => {
    if (e.key === 'Enter') {
      // Validate and commit on Enter
      if (pendingInput !== null) {
        if (hasClear && pendingInput.trim() === '') {
          // Same clearable contract as blur: Enter on an emptied input
          // commits null instead of reverting.
          if (value != null) {
            onChange(null);
          }
        } else {
          const parsed = parseNumberInput(pendingInput, {
            min,
            max,
            isIntegerOnly
          });
          if (parsed !== null && parsed !== value) {
            onChange(parsed);
          }
        }
      }
      onEnter?.();
    }
    onKeyDown?.(e);
  }, [pendingInput, value, onChange, min, max, isIntegerOnly, onEnter, onKeyDown, hasClear]);

  // Handle clear button click
  const handleClear = useCallback(() => {
    if (hasClear) {
      onChange(null);
    }
    setPendingInput(null);
    inputRef.current?.focus();
  }, [hasClear, onChange]);

  // Focus input when clicking anywhere on the wrapper (icons, padding, etc.)
  const {
    onClick: handleWrapperClick,
    onMouseUp: handleWrapperMouseUp
  } = useInputContainer({
    containerRef,
    inputRef,
    disabled: isDisabled
  });
  const inputWrapper = /*#__PURE__*/_jsxs("div", {
    ref: el => {
      containerRef.current = el;
      // Anchor + hover/focus listeners for the disabled-message tooltip.
      // Handlers are gated internally by isEnabled, and anchor names
      // compose, so attaching unconditionally is safe.
      disabledMessageTooltip.ref(el);
    },
    onClick: handleWrapperClick,
    onMouseUp: handleWrapperMouseUp,
    ...mergeProps(themeProps('number-input', {
      size,
      status: status?.type ?? null
    }), stylex.props(inputWrapperStyles.base, styles.wrapper, sizeStyles[size], isDisabled && inputWrapperStyles.disabled, status && inputStatusBorderStyles[status.type], status && inputStatusHoverShadowStyles[status.type], status && inputStatusFocusWithinStyles[status.type], inputGroup && groupStyles.inGroup, xstyle), className, style),
    children: [startIcon && renderIconSlot(startIcon, {
      size: 'sm',
      color: 'secondary'
    }), inputGroup && /*#__PURE__*/_jsx(VisuallyHidden, {
      id: inputLabelID,
      children: label
    }), /*#__PURE__*/_jsx("input", {
      ...rest,
      ref: mergeRefs(ref, inputRef),
      id: id,
      name: htmlName,
      type: "number",
      autoComplete: autoComplete,
      value: displayValue,
      onChange: handleInputChange,
      onFocus: handleFocus,
      onBlur: handleBlur,
      onKeyDown: handleKeyDown,
      placeholder: placeholder
      // With a disabledMessage the input keeps focusability via aria-disabled
      // so the reason is focus-discoverable; readOnly + the handler guards
      // keep the value from changing.
      ,
      disabled: isDisabled && !showsDisabledMessage,
      "aria-disabled": showsDisabledMessage ? 'true' : undefined,
      readOnly: showsDisabledMessage || undefined,
      autoFocus: hasAutoFocus,
      "data-autofocus": hasAutoFocus || undefined,
      min: min ?? undefined,
      max: max ?? undefined,
      step: step ?? undefined,
      "aria-describedby": ariaDescribedBy,
      "aria-required": isRequired === true ? 'true' : undefined,
      "aria-invalid": status?.type === 'error' || !isInputValid ? 'true' : undefined,
      "aria-labelledby": ariaLabelledBy,
      ...{
        0: {
          className: "x1lliihq x98rzlu xeuugli xc342km xng3xce x1717udv x9ynric xjm74w1 x6pjikd xw6l6zx x1tgivj0 xjbqb8w x1a2a7pz xeyghm5"
        },
        2: {
          className: "x1lliihq x98rzlu xeuugli xc342km xng3xce x1717udv x9ynric xjm74w1 x6pjikd xw6l6zx x1tgivj0 xjbqb8w x1a2a7pz xeyghm5 x1h6gzvc"
        },
        1: {
          className: "x1lliihq x98rzlu xeuugli xc342km xng3xce x1717udv x9ynric xjm74w1 x6pjikd xw6l6zx xjbqb8w x1a2a7pz xeyghm5 xv1l7n4"
        },
        3: {
          className: "x1lliihq x98rzlu xeuugli xc342km xng3xce x1717udv x9ynric xjm74w1 x6pjikd xw6l6zx xjbqb8w x1a2a7pz xeyghm5 x1h6gzvc xv1l7n4"
        }
      }[!!isDisabled << 1 | !!!isInputValid << 0]
    }), units && /*#__PURE__*/_jsx("span", {
      ...{
        className: "x9ynric xjm74w1 xw6l6zx xv1l7n4 x2lah0s"
      },
      children: units
    }), /*#__PURE__*/_jsx(VisuallyHidden, {
      as: "div",
      role: "alert",
      "aria-live": "assertive",
      children: !isInputValid ? 'Invalid number' : ''
    }), hasClear && value != null && !isDisabled && /*#__PURE__*/_jsx("button", {
      type: "button",
      onClick: handleClear,
      "aria-label": t('@astryx.numberInput.clearLabel', {
        label
      }),
      ...{
        className: "x78zum5 x6s0dn4 xl56j7k x1717udv x1ghz6dp xc342km xng3xce xjbqb8w x1ypdohk xh6dtrn x1a2a7pz x1p25gnr x1y3gkto"
      },
      children: /*#__PURE__*/_jsx(Icon, {
        icon: "close",
        size: "sm",
        color: "secondary"
      })
    }), status && !inputGroup && /*#__PURE__*/_jsx(Icon, {
      icon: statusIconMap[status.type],
      size: "md",
      color: statusIconColorMap[status.type]
    })]
  });
  if (inputGroup) {
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [inputWrapper, showsDisabledMessage && disabledMessageTooltip.renderTooltip(disabledMessage)]
    });
  }
  return /*#__PURE__*/_jsxs(Field, {
    label: label,
    isLabelHidden: isLabelHidden,
    description: description,
    inputID: id,
    descriptionID: description ? descriptionID : undefined,
    isOptional: isOptional,
    isRequired: isRequired,
    isDisabled: isDisabled,
    labelIcon: labelIcon,
    status: status ? {
      type: status.type,
      message: status.message,
      messageID: status.message ? statusMessageID : undefined
    } : undefined,
    labelTooltip: labelTooltip,
    width: width,
    children: [inputWrapper, showsDisabledMessage && disabledMessageTooltip.renderTooltip(disabledMessage)]
  });
}
NumberInput.displayName = 'NumberInput';